/**
 * Redux slice for merchant data management
 * Handles fetching and caching merchant details
 */

import { ApiHelperFunction } from "@funded-here-interface/common/src/Utils/HttpClient";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";

interface MerchantState {
  status: "idle" | "loading" | "success" | "failed";
  merchantDetails: any | null;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
}

const initialState: MerchantState = {
  status: "idle",
  merchantDetails: null,
  isLoading: false,
  isSuccess: false,
  isError: false,
};

/**
 * Async thunk to fetch merchant data
 * @param token - Authentication token
 */
export const getMerchant = createAsyncThunk(
  "get_all_merchant",
  async ({ token }: { token: string }) => {
    const response: any = await ApiHelperFunction({
      urlPath: "view-merchant-data",
      method: "GET",
      token: token,
    });

    if (response?.status === 200) {
      return response.data;
    } else {
      const errorMessage = response?.response?.data?.message || "Failed to fetch merchant data";
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }
  },
);

export const MerchantSlice = createSlice({
  name: "merchant",
  initialState,
  reducers: {
    /**
     * Clear merchant data and reset loading states
     */
    clearState: (state) => {
      state.merchantDetails = null;
      state.isLoading = false;
      state.isSuccess = false;
      state.isError = false;
      state.status = "idle";
    },

    /**
     * Reset to initial state
     */
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch merchant data
      .addCase(getMerchant.pending, (state) => {
        state.status = "loading";
        state.isLoading = true;
        state.isError = false;
      })
      .addCase(getMerchant.fulfilled, (state, { payload }) => {
        state.status = "success";
        state.isSuccess = true;
        state.isLoading = false;
        state.isError = false;
        state.merchantDetails = payload?.data;
      })
      .addCase(getMerchant.rejected, (state) => {
        state.status = "failed";
        state.isError = true;
        state.isSuccess = false;
        state.isLoading = false;
      });
  },
});

export const { clearState, resetState } = MerchantSlice.actions;

export default MerchantSlice.reducer;
